import React, { FC } from "react";
import Link from "next/link";
import { SupplierCardFragment } from "@graphql/types";
import { useRouter } from "next/router";
import { Card } from "@components/ui/Card";
import { Rating } from "@components/ui/Rating";
import { getAbsoluteUrl } from "@helpers/getAbsoluteUrl";
import { CardV2 } from "@components/ui/CardV2";

type Props = SupplierCardFragment & {
  hasAnyReviews?: boolean;
};

const SupplierCard: FC<Props> = ({ ...user }) => {
  const router = useRouter();
  const { query } = router;

  let supplierUrl = user.url;
  if (
    (query.t === undefined || query.t === "guides") &&
    (query.date !== "" || query.people !== "")
  ) {
    const queryParams = {
      ...(query.date !== undefined && { date: query.date }),
      ...(query.adults !== undefined && { adults: query.adults }),
      ...(query.children !== undefined && { children: query.children }),
      ...(query.infants !== undefined && { infants: query.infants }),
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const queryString = new URLSearchParams(queryParams as any).toString();
    supplierUrl = queryString ? `${supplierUrl}?${queryString}` : supplierUrl;
  }

  return (
    <Link href={getAbsoluteUrl(supplierUrl)} target="_blank">
      <CardV2>
        {/* Image with aspect ratio */}
        <CardV2.ImageContainer>
          <CardV2.Image
            key={user.id}
            src={user?.picture?.url}
            alt={user.city?.name + " Tour Guide - " + user.displayName}
          />

          {/* Country badge wrapper */}
          <div className="absolute top-2 left-2 w-full pr-2">
            <CardV2.Tag>{user.country?.name}</CardV2.Tag>
          </div>
        </CardV2.ImageContainer>

        <CardV2.Body>
          {/* Mobile layout: Name and rating side-by-side, and in PC its vertical */}
          <div
            className="flex flex-wrap sm:block items-center sm:items-center"
            style={{ minHeight: "3rem" }}
          >
            {/* User Title (50% on mobile, full on desktop) */}
            <div className="w-1/2 sm:w-full pr-2">
              <CardV2.Title
                className="overflow-hidden mb-2"
                style={{
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 1,
                  lineHeight: "1.3rem",
                  textAlign: "start",
                }}
              >
                {user.displayName}
              </CardV2.Title>
            </div>

            {/* Rating (50% on mobile, full on desktop) */}
            {user.stats.reviewsCount > 0 && (
              <div className="w-1/2 sm:w-full text-xs mb-0 lg:mb-5 flex justify-end sm:justify-start">
                {/* Mobile view: xs */}
                <div className="block lg:hidden">
                  <Rating
                    rate={user.stats.rating}
                    reviews={user.stats.reviewsCount}
                    alwaysShowDecimals={true}
                    variant="stars"
                    size="xs"
                    className="mt-0 sm:mt-2"
                    starsAndReviewsTwoRow
                  />
                </div>

                {/* Desktop view: sm */}
                <div className="hidden lg:block">
                  <Rating
                    rate={user.stats.rating}
                    reviews={user.stats.reviewsCount}
                    alwaysShowDecimals={true}
                    variant="stars"
                    size="sm"
                    className="mt-0 sm:mt-2"
                    starsAndReviewsTwoRow
                  />
                </div>
              </div>
            )}
          </div>

          {/* Description */}
          <div className="hidden lg:block">
            <Card.Description
              className={`text-clip mb-2 ${user.stats.reviewsCount > 0 ? "truncate-3" : "truncate-5"} `}
            >
              {user.biography}
            </Card.Description>
          </div>
        </CardV2.Body>
      </CardV2>
    </Link>
  );
};

export default SupplierCard;
